/**
 * Builds the `context` string sent with every open question to
 * POST /api/ai/chat. The backend model is a small local one (qwen2.5:1.5b)
 * with no tool calls, so this string IS its knowledge of the landlord's
 * portfolio: short instructions, today's date, the page the user is on, and
 * a handful of factual lines computed by snapshot.ts.
 *
 * Privacy: the snapshot only carries tenant names, plot titles, dates and
 * amounts. scrubPersonalData() runs over the final string anyway as a
 * second guard, so an email or phone number that slipped into a plot title
 * or tenant name still never reaches the model.
 */
import { formatDate, formatNaira, joinList, plural } from './format';
import type { PortfolioSnapshot } from './types';

const MAX_LIST = 4;
const MAX_CONTEXT_CHARS = 2800;

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Nigerian mobiles (0803…, +234 803…, 234803…) and other +country numbers.
const NG_PHONE_RE = /(?:\+?234|\b0)[\s-]?[789][01]\d(?:[\s-]?\d){7}\b/g;
const INTL_PHONE_RE = /\+\d[\d\s-]{7,}\d/g;

export function scrubPersonalData(text: string): string {
  return text
    .replace(EMAIL_RE, '[email removed]')
    .replace(NG_PHONE_RE, '[phone removed]')
    .replace(INTL_PHONE_RE, '[phone removed]');
}

export interface ContextOptions {
  /** Human label of the page the landlord is on, e.g. "Invoices". */
  pageLabel?: string;
  /** Exact facts for the question's topic, placed first so a small model uses them. */
  focus?: string;
}

const INSTRUCTIONS = [
  'You are Re:Loam Assistant inside farmland-rental software for a Nigerian landlord.',
  'Talk to the landlord as "you". Keep answers short: 2-4 sentences or a few bullet points.',
  'For anything about their plots, tenants, leases, invoices or payments, use only the facts below. If a fact is missing, say you do not have it and suggest the page to check. Never invent names, amounts or dates.',
  'Write money in naira like ₦120,000. For general farming or business advice you may use common knowledge, suited to Nigeria.',
].join(' ');

function plotsLine(s: PortfolioSnapshot): string {
  const p = s.plots;
  if (p.total === 0) return '- Plots: none recorded yet.';
  const vacant = p.vacantPlots.slice(0, MAX_LIST).map((v) => (v.size ? `${v.title} (${v.size})` : v.title));
  const vacantText = vacant.length ? ` Vacant: ${joinList(vacant)}.` : '';
  return `- Plots: ${plural(p.total, 'plot')}; ${p.occupied} occupied, ${p.vacant} vacant, occupancy ${p.occupancyRate}%.${vacantText}`;
}

function leasesLine(s: PortfolioSnapshot): string {
  const l = s.leases;
  const ending = l.endingSoon
    .slice(0, MAX_LIST)
    .map((e) => `${e.plot || 'a plot'} (${e.tenant}) on ${formatDate(e.endDate)}`);
  const endingText = ending.length
    ? ` Ending in the next ${l.windowDays} days: ${joinList(ending)}.`
    : ` None end in the next ${l.windowDays} days.`;
  return `- Leases: ${l.active} active, ${l.upcoming} upcoming.${endingText}`;
}

function invoicesLines(s: PortfolioSnapshot): string[] {
  const i = s.invoices;
  if (i.count === 0) return ['- Invoices: none issued yet.'];
  const lines = [
    `- Invoices: ${i.count} issued, ${formatNaira(i.billed)} billed, ${formatNaira(i.collected)} collected, ${formatNaira(i.outstanding)} still unpaid; collection rate ${i.collectionRate}%.`,
  ];
  if (i.overdue.length === 0) {
    lines.push('- Overdue: nothing is overdue.');
  } else {
    const who = i.overdueByTenant
      .slice(0, MAX_LIST)
      .map((t) => `${t.tenant} ${formatNaira(t.balance)} (${plural(t.invoiceCount, 'invoice')}, oldest ${t.oldestDaysOverdue} days late)`);
    lines.push(`- Overdue: ${plural(i.overdue.length, 'invoice')} totalling ${formatNaira(i.overdueTotal)}. ${joinList(who)}.`);
  }
  return lines;
}

function otherLines(s: PortfolioSnapshot): string[] {
  const upcoming = s.schedule.upcoming.slice(0, 3).map((a) => `${a.title} on ${formatDate(a.date)}`);
  return [
    `- Payments in the last 30 days: ${plural(s.payments.last30DaysCount, 'payment')}, ${formatNaira(s.payments.last30DaysTotal)}.`,
    `- Leads: ${s.leads.total} total, ${s.leads.unread} unread. Proposals: ${s.proposals.total} total, ${s.proposals.inReview} in review, ${s.proposals.approved} approved.`,
    upcoming.length
      ? `- Schedule (next ${s.schedule.windowDays} days): ${joinList(upcoming)}.`
      : `- Schedule: nothing dated in the next ${s.schedule.windowDays} days.`,
  ];
}

export function buildAssistantContext(snapshot: PortfolioSnapshot, options: ContextOptions = {}): string {
  const where = options.pageLabel ? ` The landlord is on the ${options.pageLabel} page.` : '';
  const parts = [
    INSTRUCTIONS,
    `Today is ${formatDate(snapshot.today)}.${where}`,
    ...(options.focus ? ['MOST RELEVANT FACTS (exact, use these first):', options.focus.replace(/\*\*/g, '')] : []),
    'FACTS:',
    plotsLine(snapshot),
    leasesLine(snapshot),
    ...invoicesLines(snapshot),
    ...otherLines(snapshot),
  ];
  const text = scrubPersonalData(parts.join('\n'));
  return text.length > MAX_CONTEXT_CHARS ? `${text.slice(0, MAX_CONTEXT_CHARS - 1)}…` : text;
}
